/* Invitations refer to one current canonical post; only server receipts change credit. */
(function (root, factory) {
  const exported = factory();
  if (typeof module === "object" && module.exports) module.exports = exported;
  else root.LouderrCoauthors = Object.freeze(exported);
})(typeof globalThis === "undefined" ? null : globalThis, function () {
  "use strict";
  const labels = { pending: "Invitation pending", accepted: "Co-author", declined: "Declined",
    withdrawn: "Withdrawn", removed: "Removed", left: "Left" };
  const actions = { accept: "Accept invitation", decline: "Decline", withdraw: "Withdraw invitation",
    remove: "Remove co-author", leave: "Leave this post", reinvite: "Invite again" };
  const detach = new Set(["decline", "withdraw", "remove", "leave"]);

  function appendByline(host, drop, hooks) {
    if (!host) return null;
    const users = (drop.coauthors || []).filter(user => user?.id && user.handle && user.id !== drop.author_id);
    host.dataset.coauthorHost = drop.id;
    const key = JSON.stringify(users);
    if (host.coauthorBylineKey === key) return null;
    host.coauthorBylineKey = key;
    for (const child of Array.from(host.children)) if (child.dataset.coauthorDrop) child.remove();
    if (!users.length) return null;
    const doc = hooks.document || document;
    const byline = doc.createElement("div");
    byline.className = "coauthor-byline";
    byline.dataset.coauthorDrop = drop.id;
    const label = doc.createElement("span"); label.textContent = "With "; byline.append(label);
    for (const user of users) {
      const link = doc.createElement("a");
      link.href = `/u/${encodeURIComponent(user.handle)}`;
      link.setAttribute("aria-label", `${user.display_name || user.handle}, @${user.handle}, co-author`);
      link.append(hooks.nameNode(user));
      link.addEventListener("click", event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); hooks.openProfile(user);
      });
      byline.append(link);
    }
    host.append(byline);
    return byline;
  }

  function refreshBylines(drop, hooks) {
    const doc = hooks.document || document;
    for (const host of doc.querySelectorAll("[data-coauthor-host]")) {
      if (host.dataset.coauthorHost === drop.id) appendByline(host, drop, hooks);
    }
  }

  function create(hooks) {
    const doc = hooks.document || document;
    const $ = id => doc.getElementById(id);
    const el = Object.fromEntries(["dialog", "post", "consent", "status", "error", "retry", "refresh", "rows",
      "capacity", "form", "handle", "search", "results", "search-status", "search-more", "inbox",
      "inbox-status", "inbox-error", "inbox-retry", "inbox-refresh", "inbox-list", "inbox-more"]
      .map(key => [key, $("coauthors-" + key)]));
    const owner = () => hooks.viewer()?.id || null;
    const writable = () => Boolean(owner() && hooks.canWrite());
    const canPerform = action => Boolean(owner() && (detach.has(action) ? hooks.canDetach?.() !== false : hooks.canWrite()));
    const canRetry = () => Boolean(owner() && (hooks.canRetry ? hooks.canRetry() : hooks.canDetach?.() !== false));
    let identity = owner(), session = null, serial = 0, inboxRequest = 0, searchRequest = 0;
    let inbox = { items: [], next: null, loading: false, loaded: false, error: "", append: false };
    let people = { items: [], next: null, query: "", loading: false };
    const attempts = new Map();
    const make = (tag, className = "", text = "") => {
      const node = doc.createElement(tag); node.className = className; node.textContent = text; return node;
    };
    function button(text, fn, className = "secondary-button") {
      const node = make("button", className, text); node.type = "button";
      node.addEventListener("click", fn); return node;
    }
    function syncIdentity() {
      if (owner() === identity) return false;
      identity = owner(); attempts.clear(); ++inboxRequest; ++searchRequest;
      inbox = { items: [], next: null, loading: false, loaded: false, error: "", append: false };
      if (el.dialog.open) el.dialog.close();
      session = null; el.rows.replaceChildren(); el.post.replaceChildren(); el.results.replaceChildren();
      paintInbox(); return true;
    }
    function current(s) { return session === s && s?.viewer === owner() && el.dialog.open; }
    function focusAfter(container, update) {
      const active = doc.activeElement;
      const key = container.contains(active) ? active?.dataset?.coauthorFocus : null;
      update();
      if (key && (!active.isConnected || doc.activeElement !== active)) {
        const replacement = Array.from(container.querySelectorAll("[data-coauthor-focus]"))
          .find(node => node.dataset.coauthorFocus === key && !node.disabled);
        (replacement || (container === el["inbox-list"] ? el["inbox-refresh"] : el.status)).focus();
      }
    }
    function validModel(model, id) {
      if (!model || model.drop_id !== id || !Array.isArray(model.items)
          || model.items.some(item => !item?.id || !item.user_id || !labels[item.state]
            || !Number.isInteger(item.version) || !Array.isArray(item.allowed_actions))) {
        throw new Error("Co-author details could not be verified. Refresh and try again.");
      }
      return model;
    }
    function postPreview(host, drop, unavailable) {
      const key = JSON.stringify([drop?.id, drop?.title, drop?.body, drop?.author, unavailable]);
      if (host.coauthorKey === key) return;
      host.coauthorKey = key; host.replaceChildren();
      if (unavailable || !drop) {
        host.append(make("p", "", "This post is unavailable. You can still end your co-author relationship."));
        return;
      }
      const link = make("a", "coauthor-post-link", drop.title || "View current post");
      link.href = `/d/${encodeURIComponent(drop.id)}`;
      link.addEventListener("click", event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault(); hooks.openPost(drop.id);
      });
      host.append(link);
      if (drop.author) {
        const author = make("p", ""); author.append(make("span", "", "Original author: "), hooks.nameNode(drop.author));
        host.append(author);
      }
      if (drop.body) host.append(make("p", "coauthor-post-copy", drop.body));
    }
    function allowed(item, model) {
      return item.allowed_actions.filter(action => actions[action]
        && (!(model.unavailable || !model.drop) || detach.has(action)));
    }
    function paintRows(s) {
      const busy = Boolean(attempts.get(s.id));
      focusAfter(el.rows, () => {
        const known = new Map(Array.from(el.rows.children).map(node => [node.dataset.invitationId, node]));
        for (const item of s.model?.items || []) {
          const availableActions = allowed(item, s.model);
          const key = JSON.stringify([item, availableActions, busy, availableActions.map(canPerform)]);
          let row = known.get(item.id);
          if (!row) { row = make("article", "coauthor-row"); row.dataset.invitationId = item.id; }
          known.delete(item.id);
          if (row.coauthorKey !== key) {
            row.coauthorKey = key; row.replaceChildren(); row.dataset.state = item.state;
            const copy = make("div", "coauthor-person");
            copy.append(item.user ? hooks.nameNode(item.user) : make("strong", "", "Unavailable account"));
            if (item.user?.handle) copy.append(make("small", "", `@${item.user.handle}`));
            copy.append(make("span", "coauthor-state", labels[item.state])); row.append(copy);
            const controls = make("div", "coauthor-actions");
            for (const action of availableActions) {
              const control = button(actions[action], () => requestAction(item, action));
              control.dataset.coauthorAction = action; control.dataset.coauthorFocus = `${item.id}:${action}`;
              control.disabled = busy || !canPerform(action); controls.append(control);
            }
            row.append(controls);
          }
          // Do not move retained rows: moving a focused element can blur it in native browsers.
          if (row.parentNode !== el.rows) el.rows.append(row);
        }
        for (const row of known.values()) row.remove();
      });
    }
    function paint() {
      const s = session; if (!current(s)) return;
      const attempt = attempts.get(s.id);
      const busy = Boolean(attempt);
      const message = attempt?.error || s.error || "";
      el.error.hidden = !message; el.error.textContent = message;
      el.retry.hidden = !attempt?.error; el.retry.disabled = Boolean(attempt?.busy || !canRetry());
      el.refresh.disabled = s.loading || Boolean(attempt?.busy);
      const gateNotice = writable() ? "" : hooks.canDetach?.() !== false
        ? "New invitations and acceptance are temporarily unavailable. You can still end an existing relationship."
        : "Co-author changes are currently unavailable.";
      el.status.textContent = attempt?.busy ? "Saving co-author change…" : s.loading ? "Loading co-authors…" : s.notice || gateNotice;
      el.dialog.setAttribute("aria-busy", String(s.loading || Boolean(attempt?.busy)));
      const model = s.model;
      el.form.hidden = !model?.can_invite || model.unavailable || !model.drop || !writable();
      el.handle.disabled = el.search.disabled = busy || !writable();
      const ownPost = model?.drop?.author_id === owner();
      el.capacity.hidden = !ownPost;
      el.capacity.textContent = ownPost
        ? `${model.items.filter(item => ["pending", "accepted"].includes(item.state)).length} of ${model.max_coauthors} co-author places used. Pending invitations reserve a place.` : "";
      el.consent.hidden = !model?.drop || Boolean(model.unavailable);
      if (model) postPreview(el.post, model.drop, model.unavailable);
      paintRows(s); paintPeople();
    }
    async function load() {
      syncIdentity(); const s = session; if (!current(s)) return;
      const request = ++s.read; s.loading = true; s.error = ""; paint();
      try {
        const result = await hooks.api(`/api/drops/${encodeURIComponent(s.id)}/coauthors`);
        if (!current(s) || request !== s.read) return;
        s.model = validModel(result, s.id);
      } catch (error) {
        if (!current(s) || request !== s.read) return;
        s.error = error.message || "Co-authors could not be loaded. Refresh to try again.";
        // Never keep actionable private rows after access is revoked.
        if ([401, 403, 404].includes(error.status)) { s.model = null; el.post.replaceChildren(); el.post.coauthorKey = null; }
      } finally {
        if (current(s) && request === s.read) { s.loading = false; paint(); }
      }
    }
    function show(id, source = null, path = null) {
      syncIdentity();
      if (!owner()) { hooks.signIn(); return; }
      if (session?.id === id && el.dialog.open) return load();
      session = { id, viewer: owner(), serial: ++serial, source, read: 0, model: null,
        loading: false, error: "", notice: "" };
      ++searchRequest; people = { items: [], next: null, query: "", loading: false };
      el.handle.value = ""; el.rows.replaceChildren(); el.post.replaceChildren(); el.post.coauthorKey = null;
      hooks.openDialog(el.dialog, path); paint();
      el.refresh.focus(); return load();
    }
    function close() {
      const previous = session; session = null; ++serial; ++searchRequest;
      if (previous?.source?.isConnected) previous.source.focus();
      else if (previous && hooks.inboxVisible()) el["inbox-refresh"].focus();
      else if (previous) hooks.restoreFocus?.(previous.id);
    }
    async function send(attempt, retry = false) {
      // An exact uncertain retry may only read its committed receipt while growth is paused.
      // The server still rejects a new operation; the client never changes this token or payload.
      if (attempt.busy || attempt.viewer !== owner() || attempts.get(attempt.id) !== attempt
          || (retry ? !attempt.error || !canRetry() : !canPerform(attempt.action))) return;
      attempt.busy = true; attempt.error = "";
      if (session?.id === attempt.id) { ++session.read; session.loading = false; }
      ++inboxRequest; inbox.loading = false; paint(); paintInbox();
      try {
        const result = await hooks.api(attempt.path, { method: "POST", csrf: true, json: attempt.payload });
        if (attempt.viewer !== owner() || attempts.get(attempt.id) !== attempt) return;
        validModel(result, attempt.id);
        attempts.delete(attempt.id);
        if (current(session) && session.id === attempt.id) {
          ++session.read; session.loading = false;
          session.model = attempt.postWithdrawn ? withoutPost(result) : result;
          session.notice = attempt.action === "invite" || attempt.action === "reinvite" ? "Invitation sent. Waiting for their response."
            : `Co-author change saved: ${actions[attempt.action]}.`;
          session.error = "";
          // A successful historical receipt does not establish current visibility.
          if (attempt.postWithdrawn) void load();
        }
        hooks.changed(attempt.id);
        if (!el.inbox.hidden && hooks.inboxVisible()) void refreshInbox();
      } catch (error) {
        if (attempt.viewer !== owner() || attempts.get(attempt.id) !== attempt) return;
        if (error.status >= 400 && error.status < 500 && ![408, 429].includes(error.status)) {
          attempts.delete(attempt.id);
          if (current(session) && session.id === attempt.id) {
            await load();
            if (current(session) && session.id === attempt.id) session.error = error.status === 409
              ? "This invitation changed. Review its current status before choosing an action again."
              : error.message || "This change was not accepted.";
          }
        } else {
          attempt.error = "The change could not be confirmed. Retry the same request to check safely. " + (error.message || "");
        }
      } finally { attempt.busy = false; paint(); paintInbox(); }
    }
    function startAttempt(action, item = null, person = null) {
      const s = session;
      if (!current(s) || !s.model || attempts.has(s.id) || !canPerform(action)) return;
      const payload = { action_token: hooks.token ? hooks.token() : crypto.randomUUID() };
      let path = `/api/drops/${encodeURIComponent(s.id)}/coauthors`;
      if (action === "invite" || action === "reinvite") {
        if (!s.model.can_invite || s.model.unavailable || !s.model.drop) return;
        payload.handle = person?.handle || item?.user?.handle;
        if (!payload.handle) return;
        if (item) payload.expected_version = item.version;
      } else {
        if (!allowed(item, s.model).includes(action)) return;
        path += `/${encodeURIComponent(item.id)}/actions`;
        payload.action = action; payload.expected_version = item.version;
      }
      const attempt = { id: s.id, viewer: owner(), action, path, payload, busy: false, error: "" };
      attempts.set(s.id, attempt); s.error = ""; s.notice = "";
      return send(attempt);
    }
    async function requestAction(item, action) {
      // Resolve the current record at activation, not the earlier rendered version.
      const s = session, fresh = s?.model?.items.find(record => record.id === item.id);
      if (!fresh || fresh.version !== item.version || !allowed(fresh, s.model).includes(action)) return;
      if (["remove", "leave"].includes(action) && !(await hooks.confirm(action === "leave"
        ? "Leave this post? Your credit and profile association will be removed. The original post stays."
        : "Remove this co-author? Their credit and profile association will be removed. The original post stays."))) return;
      return startAttempt(action, fresh);
    }
    function paintPeople() {
      const s = session; if (!current(s)) return;
      const busy = Boolean(attempts.get(s.id));
      const key = JSON.stringify([people.items, s.model?.items, busy, writable()]);
      if (el.results.coauthorKey !== key) {
        el.results.coauthorKey = key;
        focusAfter(el.results, () => {
          el.results.replaceChildren();
          for (const person of people.items) {
            const existing = s.model?.items.find(item => item.user_id === person.id);
            if (person.id === owner() || existing) continue;
            const row = make("div", "coauthor-row"); row.dataset.userId = person.id;
            const name = make("div", "coauthor-person"); name.append(hooks.nameNode(person), make("small", "", `@${person.handle}`));
            const invite = button("Invite", () => startAttempt("invite", null, person));
            invite.dataset.coauthorFocus = `person:${person.id}`;
            invite.disabled = busy || !writable() || !s.model?.can_invite;
            row.append(name, invite); el.results.append(row);
          }
        });
      }
      el["search-status"].textContent = people.loading ? "Finding people…" : people.error
        || (people.query && !el.results.children.length ? "No new people found. Existing invitations are listed below." : "");
      el["search-more"].hidden = !people.next;
      el["search-more"].disabled = people.loading || busy;
    }
    async function search(append = false) {
      const s = session; if (!current(s) || !s.model?.can_invite) return;
      const query = el.handle.value.trim().replace(/^@/, "");
      if (!query || (append && !people.next)) return;
      const request = ++searchRequest;
      const cursor = append ? people.next : null;
      if (!append) people = { items: [], next: null, query, loading: false };
      people.loading = true; people.error = ""; paintPeople();
      try {
        const params = new URLSearchParams({ type: "people", q: query, limit: "12" });
        if (cursor) params.set("people_cursor", cursor);
        const result = await hooks.api(`/api/search?${params}`);
        if (!current(s) || request !== searchRequest || el.handle.value.trim().replace(/^@/, "") !== query) return;
        if (!Array.isArray(result?.people?.items)) throw new Error("People search returned an invalid response.");
        people.items = [...new Map([...people.items, ...result.people.items].filter(user => user?.id && user.handle).map(user => [user.id, user])).values()];
        people.next = result.people.next_cursor || null;
      } catch (error) {
        if (current(s) && request === searchRequest) people.error = error.message || "Search failed. Choose Find people to retry.";
      } finally { if (current(s) && request === searchRequest) { people.loading = false; paintPeople(); } }
    }
    function paintInbox() {
      el["inbox-status"].textContent = !owner() ? "Sign in to see your co-author invitations."
        : inbox.loading ? "Loading co-author invitations…"
          : inbox.loaded && !inbox.items.length && !inbox.error ? "No pending invitations or co-authored posts." : "";
      el["inbox-error"].hidden = !inbox.error; el["inbox-error"].textContent = inbox.error;
      // Nothing to show: the section folds away (a class, so the refresh that checks `hidden` still runs).
      el.inbox?.classList?.toggle("is-empty", Boolean(owner() && inbox.loaded && !inbox.loading && !inbox.items.length && !inbox.error));
      el["inbox-retry"].hidden = !inbox.error;
      el["inbox-retry"].disabled = inbox.loading;
      el["inbox-refresh"].disabled = inbox.loading || !owner();
      el["inbox-more"].hidden = !inbox.next; el["inbox-more"].disabled = inbox.loading;
      focusAfter(el["inbox-list"], () => {
        const known = new Map(Array.from(el["inbox-list"].children).map(row => [row.dataset.invitationId, row]));
        for (const [index, item] of inbox.items.entries()) {
          const key = JSON.stringify(item); let row = known.get(item.id);
          if (!row) { row = make("article", "coauthor-inbox-row"); row.dataset.invitationId = item.id; }
          known.delete(item.id);
          if (row.coauthorKey !== key) {
            row.coauthorKey = key; row.dataset.state = item.state; row.replaceChildren();
            const preview = make("div", "coauthor-inbox-post"); postPreview(preview, item.drop, item.unavailable);
            const status = make("p", "coauthor-state", labels[item.state] || "Invitation");
            const open = button(item.state === "pending" ? "Review invitation" : "Manage co-authorship", () => show(item.drop_id || item.drop?.id, open));
            open.dataset.coauthorFocus = `inbox:${item.id}`;
            row.append(preview, status, open);
          }
          const position = el["inbox-list"].children[index];
          if (position !== row) el["inbox-list"].insertBefore(row, position || null);
        }
        for (const row of known.values()) row.remove();
      });
    }
    async function refreshInbox(append = false) {
      syncIdentity(); if (!owner()) { paintInbox(); return; }
      if (append && (inbox.loading || !inbox.next)) return;
      const viewer = owner(), request = ++inboxRequest;
      inbox.loading = true; inbox.error = ""; inbox.append = append; paintInbox();
      try {
        const params = new URLSearchParams({ limit: "15" });
        if (append) params.set("cursor", inbox.next);
        const result = await hooks.api(`/api/drop-coauthor-invitations?${params}`);
        if (viewer !== owner() || request !== inboxRequest) return;
        if (!Array.isArray(result?.items) || result.items.some(item => !item?.id || !(item.drop_id || item.drop?.id))) {
          throw new Error("Invitations returned an invalid response. Retry to load the current list.");
        }
        inbox.items = [...new Map([...(append ? inbox.items : []), ...result.items].map(item => [item.id, item])).values()];
        inbox.next = result.next_cursor || null; inbox.loaded = true;
      } catch (error) {
        if (viewer !== owner() || request !== inboxRequest) return;
        inbox.error = error.message || "Invitations could not be loaded.";
        if ([401, 403].includes(error.status)) inbox.items = [];
      } finally { if (viewer === owner() && request === inboxRequest) { inbox.loading = false; paintInbox(); } }
    }
    function withoutPost(value) {
      return { ...value, drop: null, unavailable: true, can_invite: false };
    }
    function withdraw(id) {
      syncIdentity(); if (!id || !owner()) return;
      const attempt = attempts.get(id);
      // This is local receipt metadata; the captured operation stays unchanged.
      if (attempt) attempt.postWithdrawn = true;
      const known = inbox.items.some(item => (item.drop_id || item.drop?.id) === id);
      const pending = inbox.loading;
      ++inboxRequest; inbox.loading = false;
      inbox.items = inbox.items.map(item => (item.drop_id || item.drop?.id) === id ? withoutPost(item) : item);
      if (current(session) && session.id === id) {
        ++session.read; session.loading = false;
        if (session.model) session.model = withoutPost(session.model);
        el.post.replaceChildren(); el.post.coauthorKey = null;
        ++searchRequest; people = { items: [], next: null, query: "", loading: false };
        el.handle.value = "";
        paint();
        if (!attempt?.busy) void load();
      }
      paintInbox();
      if (hooks.inboxVisible() && (known || pending)) void refreshInbox();
    }
    function event(id, privateUpdate = true) {
      if (current(session) && (!id || session.id === id) && !attempts.get(session.id)?.busy) void load();
      if (hooks.inboxVisible() && (privateUpdate || inbox.items.some(item => (item.drop_id || item.drop?.id) === id))) void refreshInbox();
    }
    el.form.addEventListener("submit", event => { event.preventDefault(); void search(); });
    el.handle.addEventListener("input", () => {
      ++searchRequest; people = { items: [], next: null, query: "", loading: false }; paintPeople();
    });
    el.refresh.addEventListener("click", load);
    el.retry.addEventListener("click", () => { const attempt = attempts.get(session?.id); if (attempt) void send(attempt, true); });
    el["search-more"].addEventListener("click", () => search(true));
    el["inbox-refresh"].addEventListener("click", () => refreshInbox());
    el["inbox-retry"].addEventListener("click", () => refreshInbox(inbox.append));
    el["inbox-more"].addEventListener("click", () => refreshInbox(true));
    el.dialog.addEventListener("close", close);
    paintInbox();
    return { show, refreshInbox, event, withdraw, syncIdentity,
      navigate() { if (el.dialog.open) el.dialog.close(); },
    };
  }
  return { create, appendByline, refreshBylines };
});
